<template>
    <hox-modal @close="close">
        <hox-loading-layer v-if="isAddingMetadataField" />
        <template #header>Add column</template>
        <MetadataFieldForm
            :has-error-submitting-form="hasErrorAddingMetadataField"
            :show-validation-errors="showValidationErrors"
            :error="formError"
            @formDataUpdate="setFormData"
            @hasValidationErrorsUpdate="setHasValidationErrors"
            @submit="addMetadataField"
        />
        <hox-alert size="small" type="info" margin-top="base">
            <template #content>
                <p>
                    Columns can be used to assign custom values to deliverables, such as targeting IDs or tracking
                    codes.
                </p>
                <p>These values will included as part of the published feeds.</p>
            </template>
        </hox-alert>
        <template #footer>
            <Button :disabled="showValidationErrors && hasValidationErrors" type="primary" @click="addMetadataField">
                Add column
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ValidationError from "hoxton-shared/errors/ValidationError";

import MetadataFieldForm from "@/components/Metadata/MetadataFieldForm.vue";
import { addMetadataField } from "@/services/Metadata";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";
import type { MetadataField } from "@/types/types";

export default defineComponent({
    components: {
        MetadataFieldForm
    },

    emits: ["close", "columnVisibilityUpdated"],

    data() {
        return {
            formData: {} as { columnName: string },
            hasErrorAddingMetadataField: false,
            hasValidationErrors: false,
            isAddingMetadataField: false,
            showValidationErrors: false,
            formError: null as null | Error | ValidationError
        };
    },
    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        }
    },

    methods: {
        async addMetadataField() {
            this.showValidationErrors = true;

            if (this.hasValidationErrors) {
                return;
            }

            this.hasErrorAddingMetadataField = false;
            this.isAddingMetadataField = true;

            try {
                const createdMetadataField = (await addMetadataField(
                    this.campaignId,
                    this.formData.columnName
                )) as MetadataField;

                this.showSuccessMessage(createdMetadataField);
                this.storeMetadataField(createdMetadataField);
            } catch (err) {
                this.formError = err as Error;
                this.hasErrorAddingMetadataField = true;
                return;
            } finally {
                this.isAddingMetadataField = false;
            }
            this.close();
        },

        close() {
            this.$emit("close");
        },

        setFormData(formData: { columnName: string }) {
            this.formData = formData;
            this.formError = null;
            this.hasErrorAddingMetadataField = false;
        },

        setHasValidationErrors(hasValidationErrors: boolean) {
            this.hasValidationErrors = hasValidationErrors;
        },

        showSuccessMessage(field: MetadataField) {
            if (this.$store.state.deliverableLibrary.metadataFieldsByFieldId[field._id]) {
                this.$snackbar.success(`The column "${field.name}" already exists`);
            } else {
                this.$snackbar.success(`The column "${field.name}" has been added to the table`);
            }
        },

        storeMetadataField(field: MetadataField) {
            // update or set the field we have in the store
            this.$store.dispatch(DeliverableLibraryAction.SetMetadataFieldsByFieldId, field);
            let visibilityUpdated = false;

            const visibleColumnKeys = this.$store.state.deliverableLibrary.table.visibleColumnKeys;
            const sortedColumnKeys = this.$store.state.deliverableLibrary.table.sortedColumnKeys;

            const existingVisibleColumnKey = visibleColumnKeys?.find(key => key === field._id);
            const existingSortedColumnKey = sortedColumnKeys?.find(key => key === field._id);

            // So that the new metadata field is immediately visible in the table we add it to the visible and sorted column keys
            // in the store. But only if it is not already there
            if (!existingVisibleColumnKey) {
                this.$store.dispatch(DeliverableLibraryAction.SetTableVisibleColumnKeys, [
                    ...(visibleColumnKeys as string[]),
                    field._id
                ]);

                visibilityUpdated = true;
            }
            if (!existingSortedColumnKey) {
                this.$store.dispatch(DeliverableLibraryAction.SetTableSortedColumnKeys, [
                    ...(sortedColumnKeys as string[]),
                    field._id
                ]);

                visibilityUpdated = true;
            }

            if (visibilityUpdated) {
                this.$emit("columnVisibilityUpdated");
            }
        }
    }
});
</script>
